import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchDevices,
  fetchDeviceDetails,
  addDevice,
  deleteDevices,
  editDevice,
} from "../../services/services";

// Initial State
const initialState = {
  devicesData: [],
  selectedDevice: null,
  isDevicesLoading: false,
  isAddingDevice: false,
  isDeletingDevice: false,
  isFetchingDeviceDetails: false,
  isEditingDevice: false,
  isError: false,
  deleteMessage: {},
  errorMessage: "",
  pagination: {
    page: 1,
    limit: 10,
  },
};

// Async Thunk for fetching devices with pagination
export const fetchDevicesAsync = createAsyncThunk(
  "devices/fetchDevices",
  async ({ page, limit, searchtext }, { rejectWithValue }) => {
    try {
      const response = await fetchDevices(page, limit, searchtext);
      return { data: response, page, limit, searchtext };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async Thunk for fetching details of a specific device (GET)
export const fetchDeviceDetailsAsync = createAsyncThunk(
  "devices/fetchDeviceDetails",
  async (id, { rejectWithValue }) => {
    try {
      const response = await fetchDeviceDetails(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async Thunk for adding a new device (POST)
export const addDeviceAsync = createAsyncThunk(
  "devices/addDevice",
  async (deviceData, { rejectWithValue }) => {
    try {
      const response = await addDevice(deviceData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async Thunk for deleting a device (DELETE)
export const deleteDeviceAsync = createAsyncThunk(
  "devices/deleteDevice",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteDevices(id);
      return { id, response };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async Thunk for updating a device (PUT)
export const editDeviceAsync = createAsyncThunk(
  "devices/editDevice",
  async ({ id, updatedData }, { rejectWithValue }) => {
    try {
      const response = await editDevice(id, updatedData);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Devices Slice
const devicesSlice = createSlice({
  name: "devices",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Devices
      .addCase(fetchDevicesAsync.pending, (state) => {
        state.isDevicesLoading = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(fetchDevicesAsync.fulfilled, (state, action) => {
        state.isDevicesLoading = false;
        state.devicesData = action.payload.data;
        state.pagination.page = action.payload.page;
        state.pagination.limit = action.payload.limit;
      })
      .addCase(fetchDevicesAsync.rejected, (state, action) => {
        state.isDevicesLoading = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Fetch Device Details
      .addCase(fetchDeviceDetailsAsync.pending, (state) => {
        state.isDevicesLoading = true;
        state.isFetchingDeviceDetails = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(fetchDeviceDetailsAsync.fulfilled, (state, action) => {
        state.isDevicesLoading = false;
        state.isFetchingDeviceDetails = false;
        state.selectedDevice = action.payload;
      })
      .addCase(fetchDeviceDetailsAsync.rejected, (state, action) => {
        state.isDevicesLoading = false;
        state.isFetchingDeviceDetails = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Add Device
      .addCase(addDeviceAsync.pending, (state) => {
        state.isDevicesLoading = true;
        state.isAddingDevice = false;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(addDeviceAsync.fulfilled, (state, action) => {
        state.isDevicesLoading = false;
        state.isAddingDevice = true;
        state.devicesData = action.payload;
      })
      .addCase(addDeviceAsync.rejected, (state, action) => {
        state.isDevicesLoading = false;
        state.isAddingDevice = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Delete Device
      .addCase(deleteDeviceAsync.pending, (state) => {
        state.isDeletingDevice = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(deleteDeviceAsync.fulfilled, (state, action) => {
        state.isDeletingDevice = false;
        state.deleteMessage = action.payload;
      })
      .addCase(deleteDeviceAsync.rejected, (state, action) => {
        state.isDeletingDevice = false;
        state.isError = true;
        state.errorMessage = action.payload;
      })

      // Edit Device
      .addCase(editDeviceAsync.pending, (state) => {
        state.isDevicesLoading = true;
        state.isEditingDevice = true;
        state.isError = false;
        state.errorMessage = "";
      })
      .addCase(editDeviceAsync.fulfilled, (state, action) => {
        state.isDevicesLoading = false;
        state.isEditingDevice = false;
        state.selectedDevice = action.payload;
      })
      .addCase(editDeviceAsync.rejected, (state, action) => {
        state.isDevicesLoading = false;
        state.isEditingDevice = false;
        state.isError = true;
        state.errorMessage = action.payload;
      });
  },
});

// Export reducer
export default devicesSlice.reducer;
